import React from "react";
import {
  Box,
  Typography,
  Paper,
  Divider,
  Link,
  Tooltip,
  IconButton,
  Avatar,
  Grid,
} from "@mui/material";

import XIcon from "@mui/icons-material/X";
import InstagramIcon from "@mui/icons-material/Instagram";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import SchoolIcon from "@mui/icons-material/School";
import LocationOnIcon from "@mui/icons-material/LocationOn";

const AthleteQuickView = ({ athlete, open }) => {
  // If no athlete data or not open, don't render anything
  if (!athlete || !open) return null;

  // Format height from inches to feet and inches
  const formatHeight = (heightInInches) => {
    if (!heightInInches) return "N/A";
    const feet = Math.floor(heightInInches / 12);
    const inches = heightInInches % 12;
    return `${feet}'${inches}"`;
  };

  // Get social media links from athlete data
  const socialMediaLinks = [
    {
      platform: "X (twitter)",
      icon: <XIcon />,
      handle: athlete.socialMedia?.twitter,
      url: athlete.socialMedia?.twitter
        ? `https://X.com/${athlete.socialMedia.twitter.replace("@", "")}`
        : "",
    },
    {
      platform: "Instagram",
      icon: <InstagramIcon />,
      handle: athlete.socialMedia?.instagram,
      url: athlete.socialMedia?.instagram
        ? `https://instagram.com/${athlete.socialMedia.instagram.replace(
            /^@/,
            ""
          )}`
        : "",
    },
  ].filter((link) => link.handle || link.url);

  // Get HUDL highlights links
  const hudlLinks = athlete.socialMedia?.hudl || athlete.hudl || [];
  const hasHudlLinks =
    Array.isArray(hudlLinks) &&
    hudlLinks.length > 0 &&
    hudlLinks.some((link) => link && link !== "");

  return (
    <Paper
      elevation={4}
      sx={{
        position: "absolute",
        zIndex: 1000,
        width: 320,
        p: 2,
        borderRadius: 2,
        boxShadow: "0 8px 16px rgba(0,0,0,0.15)",
        animation: "fadeIn 0.2s ease-in-out",
        "@keyframes fadeIn": {
          "0%": { opacity: 0, transform: "translateY(-10px)" },
          "100%": { opacity: 1, transform: "translateY(0)" },
        },
      }}
    >
      {/* Header with avatar and name */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Avatar
          src={athlete.profilePicture}
          alt={`${athlete.firstName || ""} ${athlete.lastName || ""}`}
          sx={{ width: 70, height: 70, mr: 2 }}
        />

        <Box>
          <Typography variant="h6" sx={{ fontWeight: "bold", lineHeight: 1.2 }}>
            {athlete.firstName || "Unknown"} {athlete.lastName || "Athlete"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {athlete.position || "N/A"}
            {athlete.secondaryPosition && ` / ${athlete.secondaryPosition}`}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 1.5 }} />

      {/* Key stats - Height, Weight, School, Location */}
      <Grid container spacing={1} sx={{ mb: 2 }}>
        <Grid item xs={6}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <SportsSoccerIcon
              fontSize="small"
              sx={{ mr: 1, color: "primary.main" }}
            />
            <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
              {formatHeight(athlete.height)} • {athlete.weight || "N/A"} lbs
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <SchoolIcon
              fontSize="small"
              sx={{ mr: 1, color: "primary.main" }}
            />
            <Typography variant="body2" noWrap sx={{ fontSize: "0.875rem" }}>
              {athlete.school || athlete.highSchool || "N/A"}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <LocationOnIcon
              fontSize="small"
              sx={{ mr: 1, color: "primary.main" }}
            />
            <Typography variant="body2" noWrap sx={{ fontSize: "0.875rem" }}>
              {athlete.city || athlete.address?.city || "N/A"},{" "}
              {athlete.state || athlete.address?.state || "N/A"}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* HUDL Highlights */}
      {hasHudlLinks && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2" gutterBottom>
            HUDL Highlights
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {hudlLinks.map((link, index) =>
              link && link !== "" ? (
                <Link
                  key={index}
                  href={link.startsWith("http") ? link : `https://${link}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    fontSize: "0.8rem",
                  }}
                >
                  <SportsSoccerIcon
                    fontSize="small"
                    sx={{ mr: 1, color: "primary.main" }}
                  />
                  {link.length > 35 ? `${link.substring(0, 35)}...` : link}
                </Link>
              ) : null
            )}
          </Box>
        </>
      )}

      {/* Social media links */}
      {socialMediaLinks.length > 0 && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2" gutterBottom>
            Social Media
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {socialMediaLinks.map((social, index) =>
              social.handle || social.url ? (
                <Tooltip key={index} title={social.handle || social.platform}>
                  <IconButton
                    size="small"
                    color="primary"
                    component={Link}
                    href={social.url || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ mb: 0.5 }}
                  >
                    {social.icon}
                  </IconButton>
                </Tooltip>
              ) : null
            )}
          </Box>
        </>
      )}

      {/* Academic quick stats if available */}
      {athlete.academics?.gpa && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2" gutterBottom>
            Academic Info
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                GPA: {athlete.academics.gpa}
              </Typography>
            </Box>
            {athlete.academics.satScore && (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                  SAT: {athlete.academics.satScore}
                </Typography>
              </Box>
            )}
            {athlete.academics.actScore && (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                  ACT: {athlete.academics.actScore}
                </Typography>
              </Box>
            )}
          </Box>
        </>
      )}
    </Paper>
  );
};

export default AthleteQuickView;
